import React, { useCallback, useEffect, useContext, useState } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { DateTime } from 'luxon';
import { Collection, CollectionAudience, Owner } from '@amzn/awscat-pattern-library-service-model';
import {
  Table,
  Box,
  Button,
  TextFilter,
  Header,
  CollectionPreferencesProps,
  Icon,
  Popover,
  Pagination,
  Modal,
  SpaceBetween,
  Link,
} from '@amzn/awsui-components-core';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { AppContext } from '../../contexts/AppContext';
import constants from '../../common/constants';
import { tablePaginationAriaLabels } from '../../api/UtilsApi';
import BrowserHistory from '../utils/BrowserHistory';
import RouterPaths, {
  EDIT_COLLECTION_PATH_PREFIX,
  COLLECTION_VIEWER_PREFIX,
} from '../../common/RouterPaths';
import { Preferences } from '../common/CommonComponents';
import { collectionAudienceLabelMap, CollectionOwners } from './index';

const EmptyCollection = () => {
  return (
    <Box textAlign="center" color="inherit">
      <b>{constants.MY_COLLECTION_NO_COLLECTIONS_LABEL}</b>
      <Box padding={{ bottom: 's' }} variant="p" color="inherit">
        {constants.MY_COLLECTION_NO_COLLECTIONS_DISPLAY_LABEL}
      </Box>
    </Box>
  );
};

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const MyCollection = () => {
  const appContext = useContext(AppContext);
  const userProfile = appContext?.authnInfo?.userProfile;

  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: constants.MY_COLLECTION_DEFAULT_PAGE_SIZE,
  });
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteCollectionId, setDeleteCollectionId] = useState<string>('');
  const [isDeletingCollection, setIsDeletingCollection] = useState<boolean>(false);

  const fetchCollections = useCallback(async () => {
    setIsLoading(true);
    const response = await mplServiceClient.listCollectionByUser();
    setCollections(response.collections);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const filterCollection = (item: Collection, filteringText: string) => {
    const filteringTextLowerCase = filteringText.toLocaleLowerCase().trim();
    const rowItems = [
      item.title.toLocaleLowerCase().trim(),
      item.description.toLocaleLowerCase().trim(),
    ];
    return rowItems.some((rowItem) => rowItem.includes(filteringTextLowerCase));
  };

  const { items, collectionProps, filterProps, paginationProps } = useCollection(collections, {
    filtering: {
      empty: <EmptyCollection />,
      noMatch: <EmptyCollection />,
      filteringFunction: filterCollection,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
  });

  const renderTitle = (collection: Collection) => {
    return (
      <Link variant="secondary" href={`${COLLECTION_VIEWER_PREFIX}/${collection.collectionId}`}>
        {collection.title}
      </Link>
    );
  };

  const renderEditAction = (collection: Collection) => {
    const { locked, lockedBy, collectionId } = collection;
    // @ts-expect-error TS18048 - strict migration
    if (!locked || lockedBy === userProfile.userId) {
      return (
        <div className="my-collection-tooltip">
          <Link variant="secondary" href={`${EDIT_COLLECTION_PATH_PREFIX}/${collectionId}`}>
            <Icon variant="subtle" name="edit" />
          </Link>
          <span className="my-collection-tooltip-text">Edit collection</span>
        </div>
      );
    } else {
      return (
        <Popover
          position="right"
          content={
            <div>
              <Box variant="h5">{constants.MY_COLLECTION_LOCKED_HEADER}</Box>
              <p>{constants.MY_COLLECTION_LOCKED_MESSAGE}</p>
              <p>
                <Box display="inline" color="text-status-info">
                  {lockedBy}
                </Box>
                {constants.MY_COLLECTION_LOCKED_MESSAGE_SUFFFIX}
              </p>
            </div>
          }
          dismissAriaLabel="Close"
        >
          <Icon name="lock-private" />
        </Popover>
      );
    }
  };

  const renderDeleteCollectionModal = (collectionId: string) => {
    setShowDeleteModal(true);
    setDeleteCollectionId(collectionId);
  };

  const renderDeleteCollectionIcon = (collection: Collection) => {
    // Do not allow user to delete published collection
    const { isPublished } = collection;
    const deleteIconClassName = isPublished
      ? 'my-collection-tooltip-text my-collection-tooltip-text-left'
      : '';
    return (
      <div className="my-collection-tooltip">
        <Button
          disabled={isPublished}
          onClick={() => renderDeleteCollectionModal(collection.collectionId)}
          iconName="remove"
          variant="icon"
          ariaLabel="Delete collection"
        />
        {isPublished ? (
          <span className={deleteIconClassName}>Cannot delete published collection</span>
        ) : null}
      </div>
    );
  };

  const columnDefinitions = [
    {
      id: 'edit-collection-icon',
      header: '',
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => renderEditAction(collection),
      width: 70,
    },
    {
      id: constants.MY_COLLECTION_COL_TITLE,
      header: constants.MY_COLLECTION_COL_TITLE,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => renderTitle(collection),
      sortingField: 'title',
    },
    {
      id: constants.MY_COLLECTION_COL_OWNERS,
      header: constants.MY_COLLECTION_COL_OWNERS,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => (
        // @ts-expect-error TS2322 - strict migration
        <CollectionOwners owners={collection.owners} userProfile={userProfile} />
      ),
      sortingField: 'owners',
    },
    {
      id: constants.MY_COLLECTION_COL_LAST_MODIFIED,
      header: constants.MY_COLLECTION_COL_LAST_MODIFIED,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => collection.lastModified.toLocaleString(DateTime.DATE_MED),
      sortingField: 'lastModified',
    },
    {
      id: constants.MY_COLLECTION_COL_AUDIENCE,
      header: constants.MY_COLLECTION_COL_AUDIENCE,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => collectionAudienceLabelMap.get(collection.audience),
      sortingField: 'audience',
    },
    {
      id: constants.MY_COLLECTION_COL_STATUS,
      header: constants.MY_COLLECTION_COL_STATUS,
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) =>
        collection.isPublished
          ? constants.MY_COLLECTION_COL_PUBLISHED_TEXT
          : constants.MY_COLLECTION_COL_DRAFT_TEXT,
      sortingField: 'isPublished',
    },
    {
      id: 'delete-collection',
      header: '',
      // @ts-expect-error TS7006 - strict migration
      cell: (collection) => renderDeleteCollectionIcon(collection),
      width: 70,
    },
  ];

  const handleCreateCollection = () => {
    BrowserHistory.push(RouterPaths.CREATE_COLLECTION);
  };

  const renderHeader = () => {
    return (
      <Header
        variant="h3"
        counter={`(${collections.length})`}
        actions={
          <Button variant="primary" onClick={handleCreateCollection}>
            {constants.CREATE_COLLECTION}
          </Button>
        }
      >
        Collections I own
      </Header>
    );
  };

  const deleteCollection = async () => {
    setIsDeletingCollection(true);
    await mplServiceClient.deleteCollection(deleteCollectionId);
    setIsDeletingCollection(false);
    setShowDeleteModal(false);
    setDeleteCollectionId('');
    await fetchCollections();
  };

  const renderDeleteCollectionFooter = () => {
    return (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={deleteCollection} loading={isDeletingCollection}>
            Delete
          </Button>
        </SpaceBetween>
      </Box>
    );
  };

  return (
    <>
      <Table
        {...collectionProps}
        loading={isLoading}
        columnDefinitions={columnDefinitions}
        pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
        resizableColumns
        wrapLines={preferences.wrapLines}
        items={items}
        loadingText={'Loading my collections...'}
        empty={<EmptyCollection />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder={constants.MY_COLLECTION_FILTER_LABEL}
            filteringAriaLabel={constants.MY_COLLECTION_FILTER_LABEL}
          />
        }
        header={renderHeader()}
        preferences={
          <Preferences disabled={false} preferences={preferences} setPreferences={setPreferences} />
        }
      />
      <Modal
        onDismiss={() => setShowDeleteModal(false)}
        visible={showDeleteModal}
        footer={renderDeleteCollectionFooter()}
        header="Delete collection"
        closeAriaLabel="Close modal"
      >
        Do you want to delete this collection?
      </Modal>
    </>
  );
};

export default MyCollection;
